import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Button, Input, Table } from 'reactstrap'

// Import API config object
import { tokenConfig } from '../utils/apiConfig'
import { buildLocationUrl, locationLabel } from '../utils/locationLink'

// Import event list components
import UpdateEvent from '../components/EventList/UpdateEvent'

// Import Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faPlusSquare, faTrash } from '@fortawesome/free-solid-svg-icons';

import ManageSessions from '../components/EventList/ManageSessions';

const EventList = props => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  // State tracker for Update Event modal
  const [updateEvent, setUpdateEvent] = useState(false)
  const [id, setId] = useState('')

  const [manageSessions, setManageSessions] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [hiddenStatuses, setHiddenStatuses] = useState(new Set(['Closed']))
  const [hiddenDifficulties, setHiddenDifficulties] = useState(new Set())

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  //Used to adjust view based on screen size
  useEffect(() => {
    function handleResize() {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //Getting all Events using axios
  useEffect(() => {
    getEvents()
  }, [])

  const getEvents = async () => {
    await axios.get('/api/events', tokenConfig())
      .then(res => setEvents(res.data))
      .catch(err => console.log(err))
  }

  const deleteEvent = async _id => {
    const confirm = window.confirm("Delete this Event?")
    if (!confirm) return console.log("Cancelled Request")
    await axios.delete(`/api/events/${_id}`, tokenConfig())
      .then(res => {
        setEvents(events.filter(event => event._id !== _id))
      })
      .catch(err => console.log(err))
  }

  const insertUpdatedEvent = updatedEvents => {
    setEvents(updatedEvents)
  }

  const allStatuses = [...new Set(events.map(e => e.status).filter(Boolean))]
  const allDifficulties = ['Beginner', 'Intermediate', 'Hard']

  const q = keyword.trim().toLowerCase()
  const displayedEvents = events.filter(event => {
    if (hiddenStatuses.has(event.status)) return false
    if (hiddenDifficulties.size > 0 && hiddenDifficulties.has(event.difficulty)) return false
    if (q && !event.name?.toLowerCase().includes(q) && !event.description?.toLowerCase().includes(q)) return false
    return true
  })

  const toggleStatus = status => setHiddenStatuses(prev => {
    const next = new Set(prev)
    next.has(status) ? next.delete(status) : next.add(status)
    return next
  })

  const toggleDifficulty = difficulty => setHiddenDifficulties(prev => {
    const next = new Set(prev)
    next.has(difficulty) ? next.delete(difficulty) : next.add(difficulty)
    return next
  })

  const renderStatus = status => {
    const cls = status?.toLowerCase().replace(/\s+/g, '-')
    return <span className={`status-pill status-pill--${cls}`}>{status}</span>
  }
  return (
    <div className='page-shell page-shell--wide page-section'>
      <UpdateEvent
        toggle={e => setUpdateEvent(e)}
        updateEvent={updateEvent}
        _id={id}
        events={events}
        insertUpdatedEvent={insertUpdatedEvent}
      />
      <ManageSessions manageSessions={manageSessions} toggle={() => setManageSessions(!manageSessions)} eventId={id}/>
      <div className='page-header'>
        <div>
          <span className='eyebrow'>Admin Events</span>
          <h1 className='section-title'>Manage FoBK events</h1>
          <p className='section-lead'>Review, update, and organise published activities with a cleaner event library view.</p>
        </div>
        <div className='page-header__meta'>
          <div className='metric-card'>
            <span>Total</span>
            <strong>{events.length}</strong>
          </div>
          <div className='metric-card'>
            <span>Showing</span>
            <strong>{displayedEvents.length}</strong>
          </div>
          <div className='metric-card'>
            <span>Open</span>
            <strong>{events.filter(({ status }) => status === 'Open').length}</strong>
          </div>
        </div>
      </div>

      <div className='page-header' style={{ marginTop: '-0.25rem' }}>
        <div />
        <Button color='primary' onClick={() => navigate('/eventlist/new')}>
          <FontAwesomeIcon icon={faPlusSquare} style={{ marginRight: '0.55rem' }} />
          New Event
        </Button>
      </div>

      <div className='fobk-surface-card management-controls' style={{ marginBottom: '1rem' }}>
        {allStatuses.length > 0 && (
          <div className='event-filter-row'>
            <span className='event-filter-label'>Status</span>
            <div className='filter-pills'>
              {allStatuses.map(status => (
                <button
                  key={status}
                  type='button'
                  className={`filter-pill${hiddenStatuses.has(status) ? ' is-inactive' : ''}`}
                  onClick={() => toggleStatus(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        )}
        {allDifficulties.length > 0 && (
          <div className='event-filter-row'>
            <span className='event-filter-label'>Difficulty</span>
            <div className='filter-pills'>
              {allDifficulties.map(diff => (
                <button
                  key={diff}
                  type='button'
                  className={`filter-pill${hiddenDifficulties.has(diff) ? ' is-inactive' : ''}`}
                  onClick={() => toggleDifficulty(diff)}
                >
                  {diff}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className='text-filter-row' style={{ marginTop: allStatuses.length || allDifficulties.length ? '1rem' : 0 }}>
          <Input
            type='text'
            placeholder='Search by name or description...'
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />
          {keyword && (
            <button type='button' className='text-filter-clear' onClick={() => setKeyword('')}>×</button>
          )}
        </div>
      </div>

      <div className='fobk-surface-card table-shell'>
        <Table className='fobk-data-table'>
          <thead>
            <tr>
              <th style={{ width: "12.5%" }} className='rowAlign'>Name</th>
              {screenWidth > 1000 && <th style={{ width: "27%" }} className='rowAlign'>Description</th>}
              <th style={{ width: "8%" }} className='rowAlign'>Capacity</th>
              <th style={{ width: "8%" }} className='rowAlign'>Difficulty</th>
              <th style={{ width: "8%" }} className='rowAlign'>Location</th>
              <th style={{ width: "8%" }} className='rowAlign'>Status</th>
              <th style={{ width: "8%" }} className='rowAlign'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedEvents.map(({ _id, name, description, location, locationPlaceId, status, difficulty, capacity }) => {
              return (
                <tr key={_id}>
                  <td className='rowAlign'>{name}</td>
                  {screenWidth > 1000 && <td className='rowAlign'>{description}</td>}
                  <td className='rowAlign'>{capacity}</td>
                  <td className='rowAlign'>{difficulty}</td>
                  <td className='rowAlign'>
                    {location
                      ? <a href={buildLocationUrl(location, locationPlaceId)} target='_blank' rel='noreferrer'>{locationLabel(location)}</a>
                      : <span className='text-muted'>—</span>}
                  </td>
                  <td className='rowAlign'>{renderStatus(status)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                      <FontAwesomeIcon
                        className='cursor'
                        icon={faEye}
                        onClick={() => {
                          setId(_id)
                          setManageSessions(true)
                        }}
                      />
                      <FontAwesomeIcon
                        className='cursor'
                        style={{ color: 'var(--fobk-forest-800)' }}
                        icon={faEdit}
                        onClick={() => {
                          setId(_id)
                          setUpdateEvent(true)
                        }}
                      />
                      <FontAwesomeIcon
                        className='cursor'
                        style={{ color: '#9e3d3d' }}
                        icon={faTrash}
                        onClick={() => deleteEvent(_id)}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

export default EventList
